/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../api/api';
import { getTokenData } from 'src/utils/api';
import { LOADING_STATUSES } from '../../../constants/statuses';
import { isResponseSuccess, formatFilters } from '../../../utils/api';
import { INITIAL_FILTERS_STATE } from '../constants/networkPresets';
import { loadPresetCategories } from '../../presetCategories/features/presetCategoriesSlice';
import { CATEGORIES_FOR_PRESETS_TYPE } from '../../presetCategories/constants/presetCategories';

export const loadNetworkPresets = createAsyncThunk(
  'networkPresets/loadNetworkPresets',
  async (data, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const { filters, page, per } = getState().networkPresetsReducer;
      const formattedFilters = formatFilters(filters);
      await dispatch(loadPresetCategories({ type: CATEGORIES_FOR_PRESETS_TYPE.network }));
      const response = await API(getTokenData()).NETWORK_PRESETS.load({
        ...formattedFilters,
        page: page + 1,
        per,
      });
      if (isResponseSuccess(response.status)) {
        return {
          data: response.data,
          totalItems: response.total,
        };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const loadAllNetworkPresets = createAsyncThunk(
  'networkPresets/loadAllNetworkPresets',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().networkPresetsReducer;
    const { filters, per } = state;
    const formattedFilters = formatFilters(filters);
    const data = [];

    const load = async (page = 1) => {
      const response = await API(getTokenData()).NETWORK_PRESETS.load({
        ...formattedFilters,
        page,
        per,
      });

      if (!isResponseSuccess(response?.status) || !response?.data) {
        throw new Error('Failed to fetch data');
      }

      data.push(...response.data);

      const total = Number(response.total) || response.data.length;
      const totalPages = Math.ceil(total / per);

      return page < totalPages ? load(page + 1) : { data, totalItems: total };
    };

    try {
      return await load();
    } catch (error) {
      return rejectWithValue(error?.toString?.() || 'Unknown error');
    }
  },
);

export const loadNetworkPresetsById = createAsyncThunk(
  'networkPresets/loadNetworkPresetsById',
  async ({
    id,
  }, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(loadPresetCategories({ type: CATEGORIES_FOR_PRESETS_TYPE.network }));
      const response = await API(getTokenData()).NETWORK_PRESETS.loadById(id);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const createNetworkPresets = createAsyncThunk(
  'networkPresets/createNetworkPresets',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).NETWORK_PRESETS.create(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const updateNetworkPresets = createAsyncThunk(
  'networkPresets/updateNetworkPresets',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).NETWORK_PRESETS.update(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const deleteNetworkPresets = createAsyncThunk(
  'networkPresets/deleteNetworkPresets',
  async (id, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).NETWORK_PRESETS.delete(id);
      if (isResponseSuccess(response.status)) {
        dispatch(loadNetworkPresets());
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

const initialState = {
  entity: [],
  page: 0,
  per: 100,
  totalItems: 0,
  status: LOADING_STATUSES.IDLE,
  error: null,
  selected: {},
  selectedStatus: LOADING_STATUSES.IDLE,
  filters: INITIAL_FILTERS_STATE,
  allItems: [],
  allItemsStatus: LOADING_STATUSES.IDLE,
};

const networkPresetsSlice = createSlice({
  name: 'networkPresets',
  initialState,
  reducers: {
    setSelectedNetworkPreset: (state, action) => {
      state.selected = action.payload;
    },
    setPageData: (state, action) => {
      state.page = action.payload.page;
      state.per = action.payload.per;
    },
    resetNetworkPresets: () => initialState,
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadNetworkPresets.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING;
      })
      .addCase(loadNetworkPresets.fulfilled, (state, action) => {
        state.entity = action.payload?.data || [];
        state.totalItems = action.payload?.totalItems || 0;
        state.status = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadNetworkPresets.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED;
      })
      .addCase(loadNetworkPresetsById.pending, (state) => {
        state.selectedStatus = LOADING_STATUSES.LOADING;
      })
      .addCase(loadNetworkPresetsById.fulfilled, (state, action) => {
        state.selected = action.payload || {};
        state.selectedStatus = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadNetworkPresetsById.rejected, (state) => {
        state.selectedStatus = LOADING_STATUSES.FAILED;
      })
      .addCase(loadAllNetworkPresets.pending, (state) => {
        state.allItems = [];
        state.allItemsStatus = LOADING_STATUSES.LOADING;
      })
      .addCase(loadAllNetworkPresets.fulfilled, (state, action) => {
        state.allItems = action.payload?.data || [];
        state.allItemsStatus = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadAllNetworkPresets.rejected, (state) => {
        state.allItems = [];
        state.allItemsStatus = LOADING_STATUSES.FAILED;
      });
  },
});

export const {
  setPageData,
  resetNetworkPresets,
  setSelectedNetworkPreset,
  setFilters,
} = networkPresetsSlice.actions;
export default networkPresetsSlice.reducer;

export const networkPresetsChangePageAction = createAsyncThunk(
  'networkPresets/networkPresetsChangePageAction',
  async ({
    page = 0,
    per = 0,
  }, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setPageData({
        page,
        per,
      }));
      await dispatch(loadNetworkPresets());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const networkPresetsFilterApply = createAsyncThunk(
  'networkPresets/filterApply',
  async (newFilters, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setFilters({
        ...newFilters,
      }));
      await dispatch(loadNetworkPresets());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);
